import { StrictMode, type ReactNode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ListOrdered,
  LogOut,
  Search,
  SquarePen,
  UserRound,
  X,
} from "lucide-react";
import {
  addMemberMemo,
  answerInquiry,
  cancelMemberSubscription,
  cancelPaymentSubscription,
  deleteMemberMemo,
  getInquiries,
  getInquiry,
  getMember,
  getMembers,
  getPayment,
  getPayments,
  getRecommendation,
  getRecommendations,
  getSavedRecipe,
  getSavedRecipes,
  refundPayment,
  updateMember,
  type AdminCredentials,
  type InquiryDetail,
  type InquirySummary,
  type MemberDetail,
  type MemberSummary,
  type PaymentDetail,
  type PaymentSummary,
  type RecommendationDetail,
  type RecommendationSummary,
  type SavedRecipeDetail,
  type SavedRecipeSummary,
} from "../shared/admin-api";
import "../styles.css";

type View =
  | "login"
  | "members"
  | "dietHistories"
  | "dietDetail"
  | "recipes"
  | "recipeDetail"
  | "subscriptions"
  | "subscriptionDetail"
  | "inquiries"
  | "inquiryDetail";

type MenuView = "members" | "dietHistories" | "recipes" | "subscriptions" | "inquiries";

interface MenuItem {
  view: MenuView;
  label: string;
  icon: typeof UserRound;
}

const menus: MenuItem[] = [
  { view: "members", label: "회원 관리", icon: UserRound },
  { view: "dietHistories", label: "Ai 식단 추천 이력 관리", icon: ListOrdered },
  { view: "recipes", label: "레시피 저장 이력 관리", icon: ListOrdered },
  { view: "subscriptions", label: "구독 결제 관리", icon: ListOrdered },
  { view: "inquiries", label: "문의 관리", icon: SquarePen },
];

const titles: Record<View, string> = {
  login: "Admin 로그인",
  members: "회원 관리",
  dietHistories: "Ai 식단 추천 이력 관리",
  dietDetail: "Ai 식단 추천 상세 정보",
  recipes: "레시피 저장 이력 관리",
  recipeDetail: "레시피 상세 정보",
  subscriptions: "구독 결제 관리",
  subscriptionDetail: "구독 상세",
  inquiries: "문의 관리",
  inquiryDetail: "문의 관리",
};

const ADMIN_CREDENTIALS_KEY = "betterTableAdminCredentials";

function readAdminCredentials(): AdminCredentials | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(ADMIN_CREDENTIALS_KEY) ?? "null") as AdminCredentials | null;
    return parsed?.id && parsed.password ? parsed : null;
  } catch {
    return null;
  }
}

function formatDate(value: string | null | undefined, dateOnly = false) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", dateOnly
    ? { year: "numeric", month: "2-digit", day: "2-digit" }
    : { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }
  ).format(date).replace(/\. /g, ".").replace(/\.$/, "");
}

function memberStatus(value: string) {
  return ({ ACTIVE: "정상", SUSPENDED: "비활성", WITHDRAWN: "탈퇴" } as Record<string, string>)[value] ?? value;
}

function genderLabel(value: string | null) {
  return ({ MALE: "남성", FEMALE: "여성" } as Record<string, string>)[value ?? ""] ?? value ?? "-";
}

function diningPartySizeLabel(value: string | null, eatsAlone: boolean | null, profileCompletedAt: string | null) {
  const label = ({ ONE: "1인", TWO: "2인", THREE_TO_FOUR: "3~4인", FIVE_PLUS: "5인 이상" } as Record<string, string>)[value ?? ""];
  if (label) return label;
  if (eatsAlone === true) return "1인";
  if (eatsAlone === false || profileCompletedAt) return "2인";
  return "-";
}

function paymentStatus(value: string) {
  return ({ READY: "결제 대기", PENDING: "결제 진행", DONE: "결제 완료", FAILED: "결제 실패", CANCELLED: "결제 취소", REFUNDED: "환불 완료" } as Record<string, string>)[value] ?? value;
}

function inquiryStatus(value: string) {
  return value === "ANSWERED" ? "Y" : "N";
}

function inquiryCategory(value: string) {
  return ({
    AI_RECOMMENDATION: "AI식단 추천 문의",
    RECIPE: "레시피 문의",
    SERVICE: "서비스 이용 문의",
    OTHER: "기타 문의",
  } as Record<string, string>)[value] ?? value;
}

function pageQuery(page: number, filters: Record<string, string>) {
  const params = new URLSearchParams({ page: String(page), size: "10" });
  Object.entries(filters).forEach(([key, value]) => {
    if (value.trim()) params.set(key, value.trim());
  });
  return `?${params.toString()}`;
}

function detailCode() {
  return new URLSearchParams(window.location.search).get("code") ?? "";
}

function viewFromUrl(): View {
  const value = new URLSearchParams(window.location.search).get("view") as View | null;
  return value && value in titles ? value : "login";
}

function navigate(view: View, code?: string, context?: { memberCode?: string }) {
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);
  if (code) url.searchParams.set("code", code);
  else url.searchParams.delete("code");
  if (context?.memberCode) url.searchParams.set("memberCode", context.memberCode);
  else url.searchParams.delete("memberCode");
  window.history.pushState({ view }, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function parentMenu(view: View): MenuView {
  if (view === "dietDetail") return "dietHistories";
  if (view === "recipeDetail" && new URLSearchParams(window.location.search).has("memberCode")) return "members";
  if (view === "recipeDetail") return "recipes";
  if (view === "subscriptionDetail") return "subscriptions";
  if (view === "inquiryDetail") return "inquiries";
  return view === "login" ? "members" : view;
}

function AdminLogin() {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const enabled = Boolean(id.trim() && password.trim());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  return (
    <main className="bt-bo-login">
      <form
        className="bt-bo-login-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!enabled || submitting) return;
          setSubmitting(true);
          setError("");
          const credentials = { id: id.trim(), password };
          try {
            await getMembers(credentials, "?page=0&size=1");
            sessionStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(credentials));
            navigate("members");
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : "로그인 정보를 확인해 주세요.");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <h1>Admin 로그인</h1>
        <div className="bt-bo-login-fields">
          <input aria-label="아이디" value={id} onChange={(event) => setId(event.target.value)} placeholder="아이디를 입력해 주세요." />
          <input aria-label="비밀번호" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요." />
        </div>
        {error ? <p role="alert">{error}</p> : null}
        <button type="submit" disabled={!enabled || submitting}>{submitting ? "확인 중" : "로그인"}</button>
      </form>
    </main>
  );
}

function AdminShell({ view, children }: { view: View; children: ReactNode }) {
  const active = parentMenu(view);
  return (
    <main className="bt-bo-shell">
      <aside className="bt-bo-sidebar">
        <div className="bt-bo-sidebar-spacer" />
        <nav aria-label="관리자 메뉴">
          {menus.map(({ view: menuView, label, icon: Icon }) => (
            <button className={active === menuView ? "is-active" : ""} type="button" key={menuView} onClick={() => navigate(menuView)}>
              <Icon aria-hidden="true" size={24} strokeWidth={1.5} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <button className="bt-bo-logout" type="button" onClick={() => { sessionStorage.removeItem(ADMIN_CREDENTIALS_KEY); navigate("login"); }}>
          <LogOut aria-hidden="true" size={24} strokeWidth={1.5} />
          <span>로그아웃</span>
        </button>
      </aside>
      <section className="bt-bo-workspace">
        <header className="bt-bo-gnb"><h1>{titles[view]}</h1></header>
        <div className="bt-bo-page">{children}</div>
      </section>
    </main>
  );
}

function Field({ label, type = "text", placeholder, selectOptions, value = "", onChange }: {
  label: string;
  type?: string;
  placeholder?: string;
  selectOptions?: string[];
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label className="bt-bo-field">
      <span>{label}</span>
      {selectOptions ? (
        <span className="bt-bo-select-wrap">
          <select value={value || selectOptions[0]} onChange={(event) => onChange?.(event.target.value)}>{selectOptions.map((option) => <option key={option}>{option}</option>)}</select>
          <ChevronDown aria-hidden="true" size={16} />
        </span>
      ) : (
        <span className="bt-bo-input-wrap">
          {type === "date" ? <CalendarDays aria-hidden="true" size={16} /> : <Search aria-hidden="true" size={16} />}
          <input type={type} placeholder={placeholder} value={value} onChange={(event) => onChange?.(event.target.value)} />
        </span>
      )}
    </label>
  );
}

function SearchPanel({ title, children, onSearch }: { title: string; children: ReactNode; onSearch?: () => void }) {
  return (
    <section className="bt-bo-search-section">
      <h2>{title}</h2>
      <div className="bt-bo-search-card">{children}</div>
      <button className="bt-bo-primary" type="button" onClick={onSearch}>조회</button>
    </section>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (page: number) => void }) {
  if (totalPages <= 1) return null;

  const visibleCount = Math.min(totalPages, 6);
  const start = Math.min(Math.max(page - Math.floor(visibleCount / 2), 0), totalPages - visibleCount);
  const pages = Array.from({ length: visibleCount }, (_, index) => start + index);

  return (
    <nav className="bt-bo-pagination" aria-label="페이지 이동">
      <button type="button" aria-label="이전 페이지" disabled={page === 0} onClick={() => onChange(page - 1)}><ChevronLeft size={16} /></button>
      {pages.map((pageNumber) => (
        <button
          className={pageNumber === page ? "is-active" : ""}
          type="button"
          key={pageNumber}
          aria-current={pageNumber === page ? "page" : undefined}
          onClick={() => onChange(pageNumber)}
        >{pageNumber + 1}</button>
      ))}
      <button type="button" aria-label="다음 페이지" disabled={page >= totalPages - 1} onClick={() => onChange(page + 1)}><ChevronRight size={16} /></button>
    </nav>
  );
}

function DataTable({ headings, rows, selectedIndex, onSelect, onOpen, checkboxes = false, className = "", columnWidths }: {
  headings: string[];
  rows: string[][];
  selectedIndex?: number | null;
  onSelect?: (index: number) => void;
  onOpen?: (index: number) => void;
  checkboxes?: boolean;
  className?: string;
  columnWidths?: string[];
}) {
  return (
    <div className="bt-bo-table-wrap">
      <table className={`bt-bo-table ${className}`.trim()}>
        {columnWidths ? <colgroup>{checkboxes ? <col style={{ width: 44 }} /> : null}{columnWidths.map((width, index) => <col key={`${width}-${index}`} style={{ width }} />)}</colgroup> : null}
        <thead><tr>{checkboxes ? <th className="is-check"><input type="checkbox" aria-label="전체 선택" /></th> : null}{headings.map((heading) => <th key={heading}>{heading}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr className={selectedIndex === index ? "is-selected" : ""} key={`${row[0]}-${index}`} onClick={() => onSelect?.(index)} onDoubleClick={() => onOpen?.(index)}>
              {checkboxes ? <td className="is-check" onClick={(event) => event.stopPropagation()}><input type="checkbox" aria-label={`${row[0]} 선택`} /></td> : null}
              {row.map((cell, cellIndex) => <td key={`${cellIndex}-${cell}`} title={cell}><span className={cell.includes("취소") ? "is-cancelled" : cell.includes("완료") || cell === "N" ? "is-blue" : ""}>{cell}</span></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="bt-bo-section-title"><h2>{children}</h2>{action}</div>;
}

function InfoGrid({ rows, className = "" }: { rows: Array<Array<[string, ReactNode]>>; className?: string }) {
  return (
    <div className={`bt-bo-info-grid ${className}`.trim()}>
      {rows.map((row, rowIndex) => (
        <div className={`bt-bo-info-row has-${row.length}`} key={rowIndex}>
          {row.map(([label, value], index) => (
            <div className="bt-bo-info-cell" key={`${rowIndex}-${index}-${label}`}><span>{label}</span><div>{value}</div></div>
          ))}
        </div>
      ))}
    </div>
  );
}

function BackButton({ target }: { target: View }) {
  return <button className="bt-bo-back" type="button" onClick={() => navigate(target)}>뒤로</button>;
}

function AdminModal({ title, description, confirmLabel = "확인", cancelLabel, onConfirm, onCancel }: {
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}) {
  return (
    <div className="bt-bo-modal-backdrop" role="presentation" onMouseDown={onCancel}>
      <section className="bt-bo-modal" role="dialog" aria-modal="true" aria-labelledby="bt-bo-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <h2 id="bt-bo-modal-title">{title}</h2>
        <div className="bt-bo-modal-copy">{description}</div>
        <div className="bt-bo-modal-actions">
          {cancelLabel ? <button className="bt-bo-secondary" type="button" onClick={onCancel}>{cancelLabel}</button> : null}
          <button className="bt-bo-primary" type="button" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </section>
    </div>
  );
}

function MembersPage() {
  const credentials = readAdminCredentials()!;
  const [items, setItems] = useState<MemberSummary[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | null>(() => detailCode() || null);
  const [detail, setDetail] = useState<MemberDetail | null>(null);
  const [memberRecipes, setMemberRecipes] = useState<SavedRecipeSummary[]>([]);
  const [memberRecipesLoading, setMemberRecipesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [memberCodeInput, setMemberCodeInput] = useState("");
  const [memberNameInput, setMemberNameInput] = useState("");
  const [memberPhoneInput, setMemberPhoneInput] = useState("");
  const [memberStatusInput, setMemberStatusInput] = useState("전체");
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});
  const [memoInput, setMemoInput] = useState("");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editStatus, setEditStatus] = useState<MemberSummary["status"]>("ACTIVE");
  const [modal, setModal] = useState<"saved" | "passwordConfirm" | "passwordDone" | "memoDelete" | "memoDeleted" | "subscriptionCancel" | "subscriptionCancelled" | null>(null);
  const [memoDeleteId, setMemoDeleteId] = useState<number | null>(null);

  const loadMembers = async (pageNumber = pageIndex) => {
    setLoading(true);
    setError("");
    try {
      const page = await getMembers(credentials, pageQuery(pageNumber, searchFilters));
      setItems(page.items);
      setTotal(page.total);
      setTotalPages(page.totalPages);
      if (page.page !== pageNumber) setPageIndex(page.page);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "회원 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadMembers(pageIndex); }, [pageIndex, searchFilters]);
  useEffect(() => {
    if (!selectedCode) { setDetail(null); setMemberRecipes([]); return; }
    void getMember(credentials, selectedCode).then((value) => {
      setDetail(value);
      setEditName(value.name);
      setEditPhone(value.phone ?? "");
      setEditEmail(value.email);
      setEditStatus(value.status);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "회원 상세정보를 불러오지 못했습니다."));
    setMemberRecipesLoading(true);
    void getSavedRecipes(credentials, `?memberCode=${encodeURIComponent(selectedCode)}&page=0&size=100`)
      .then((page) => setMemberRecipes(page.items))
      .catch((reason) => setError(reason instanceof Error ? reason.message : "회원 레시피 정보를 불러오지 못했습니다."))
      .finally(() => setMemberRecipesLoading(false));
  }, [selectedCode]);

  const rows = items.map((member, index) => [
    String(total - pageIndex * 10 - index).padStart(2, "0"), member.memberCode, member.name, member.phone ?? "-",
    member.email, formatDate(member.joinedAt, true), memberStatus(member.status),
  ]);
  const selected = selectedCode ? items.findIndex((item) => item.memberCode === selectedCode) : null;
  const memberRecipeRows = memberRecipes.map((recipe, index) => [
    String(memberRecipes.length - index).padStart(2, "0"), recipe.savedRecipeCode,
    formatDate(recipe.savedAt), recipe.recipeTitle,
  ]);
  return (
    <>
      <SearchPanel title="회원 검색" onSearch={() => {
        setSelectedCode(null);
        setPageIndex(0);
        setSearchFilters({
          memberCode: memberCodeInput,
          name: memberNameInput,
          phone: memberPhoneInput,
          status: memberStatusInput === "정상" ? "ACTIVE" : memberStatusInput === "비활성" ? "SUSPENDED" : "",
        });
      }}>
        <div className="bt-bo-fields bt-bo-member-fields">
          <Field label="회원 코드" placeholder="ex. BTYYYYMMDD05" value={memberCodeInput} onChange={setMemberCodeInput} />
          <Field label="회원명" placeholder="ex. 홍길동" value={memberNameInput} onChange={setMemberNameInput} />
          <Field label="휴대폰 번호" placeholder="ex. 01012345678" value={memberPhoneInput} onChange={setMemberPhoneInput} />
          <Field label="회원 상태" selectOptions={["전체", "정상", "비활성"]} value={memberStatusInput} onChange={setMemberStatusInput} />
        </div>
      </SearchPanel>
      <section className="bt-bo-list-section">
        <SectionTitle>회원 리스트</SectionTitle>
        {loading ? <p>회원 정보를 불러오는 중입니다.</p> : null}
        {error ? <p role="alert">{error}</p> : null}
        <DataTable
          className="bt-bo-member-table"
          headings={["No", "회원 코드", "회원명", "휴대폰 번호", "이메일", "가입일", "상태"]}
          rows={rows}
          selectedIndex={selected}
          onSelect={(index) => setSelectedCode(items[index]?.memberCode ?? null)}
          columnWidths={["60px", "160px", "110px", "160px", "auto", "140px", "84px"]}
        />
        <Pagination page={pageIndex} totalPages={totalPages} onChange={(page) => { setSelectedCode(null); setPageIndex(page); }} />
      </section>
      {detail ? (
        <section className="bt-bo-detail-stack">
          <SectionTitle>회원 상세정보</SectionTitle>
          <InfoGrid className="bt-bo-member-info" rows={[
            [["회원 코드", detail.memberCode], ["회원 ID", detail.loginId ?? "-"], ["회원명", <input value={editName} onChange={(event) => setEditName(event.target.value)} />]],
            [["가입일", formatDate(detail.joinedAt, true)], ["휴대폰 번호", <input value={editPhone} onChange={(event) => setEditPhone(event.target.value)} />], ["이메일", <input value={editEmail} onChange={(event) => setEditEmail(event.target.value)} />]],
            [["상태", <select value={editStatus} onChange={(event) => setEditStatus(event.target.value as MemberSummary["status"])}><option value="ACTIVE">정상</option><option value="SUSPENDED">비활성</option><option value="WITHDRAWN">탈퇴</option></select>], ["구독 여부", <div className="bt-bo-inline-action"><span>{detail.subscriptionStatus === "PAID" ? "구독중" : detail.subscriptionStatus === "FREE_TRIAL" ? "무료체험" : detail.subscriptionCancelledAt ? "구독취소" : "미구독"}</span>{detail.subscriptionStatus === "PAID" ? <button className="bt-bo-secondary bt-bo-compact" type="button" onClick={() => setModal("subscriptionCancel")}>구독취소</button> : null}</div>], ["가입 구분", detail.authProvider === "KAKAO" ? "카카오" : "이메일"]],
          ]} />
          <div className="bt-bo-memo-table">
            <span className="bt-bo-memo-label">메모</span>
            <div className="bt-bo-memo-content">
              <div className="bt-bo-memo-entry">
                <input id="member-memo" aria-label="회원 메모" value={memoInput} onChange={(event) => setMemoInput(event.target.value)} placeholder="회원 관련 메모를 입력해 주세요." />
                <button className="bt-bo-primary" type="button" disabled={!memoInput.trim() || detail.memos.length >= 4} onClick={async () => {
                  try {
                    const memo = await addMemberMemo(credentials, detail.memberCode, memoInput.trim());
                    setDetail((value) => value ? { ...value, memos: [memo, ...value.memos] } : value);
                    setMemoInput("");
                  } catch (reason) { setError(reason instanceof Error ? reason.message : "메모를 등록하지 못했습니다."); }
                }}>등록</button>
              </div>
              <p>메모는 최대 4개까지 등록할 수 있습니다.</p>
              <ul>{detail.memos.map((memo) => <li key={memo.id}><span>{memo.content}</span><time>{formatDate(memo.createdAt)}</time><button type="button" aria-label="메모 삭제" onClick={() => { setMemoDeleteId(memo.id); setModal("memoDelete"); }}><X size={16} /></button></li>)}</ul>
            </div>
          </div>
          <div className="bt-bo-center"><button className="bt-bo-primary" type="button" onClick={async () => {
            try {
              await updateMember(credentials, detail.memberCode, { name: editName, phone: editPhone, email: editEmail, status: editStatus });
              await loadMembers();
              setModal("saved");
            } catch (reason) { setError(reason instanceof Error ? reason.message : "회원 정보를 저장하지 못했습니다."); }
          }}>저장</button></div>
          <SectionTitle>회원 프로필정보</SectionTitle>
          <InfoGrid className="bt-bo-profile-grid" rows={[
            [["프로필 이미지", <div className="bt-bo-profile-image"><span>{detail.profile.profileImageKey ? "등록 완료" : "미등록"}</span></div>], ["생년월일", detail.profile.birthDate ?? "-"], ["키", detail.profile.heightCm ? `${detail.profile.heightCm}cm` : "-"]],
            [["체중", detail.profile.weightKg ? `${detail.profile.weightKg}kg` : "-"], ["성별", genderLabel(detail.profile.gender)], ["활동량", detail.profile.activityLevel ?? "-"]],
            [["건강 목표", detail.profile.healthGoal ?? "-"], ["질환", detail.profile.diseases ?? "-"], ["알레르기 및 기피 식재료", detail.profile.allergyFoods ?? "-"]],
            [["음식 취향", detail.profile.foodPreferences ?? "-"], ["함께 식사하는 인원", diningPartySizeLabel(detail.profile.diningPartySize, detail.profile.eatsAlone, detail.profile.profileCompletedAt)]],
          ]} />
          <SectionTitle>회원 레시피정보</SectionTitle>
          {memberRecipesLoading ? <p>회원 레시피 정보를 불러오는 중입니다.</p> : null}
          {!memberRecipesLoading && memberRecipes.length === 0 ? <p className="bt-bo-empty">저장한 레시피가 없습니다.</p> : null}
          {memberRecipes.length > 0 ? <DataTable
            className="bt-bo-member-recipe-table"
            headings={["No", "저장 코드", "저장 일시", "레시피명"]}
            rows={memberRecipeRows}
            onSelect={(index) => navigate("recipeDetail", memberRecipes[index]?.savedRecipeCode, { memberCode: detail.memberCode })}
            columnWidths={["60px", "190px", "180px", "auto"]}
          /> : null}
        </section>
      ) : null}
      {modal === "saved" ? <AdminModal title="저장 완료" description="회원 정보가 저장되었습니다." onConfirm={() => setModal(null)} /> : null}
      {modal === "passwordConfirm" ? <AdminModal title="비밀번호 초기화" description="회원 인증 API 구현 단계에서 연결됩니다." confirmLabel="확인" onConfirm={() => setModal(null)} /> : null}
      {modal === "passwordDone" ? <AdminModal title="비밀번호 초기화 완료" description="비밀번호가 초기화되었습니다." onConfirm={() => setModal(null)} /> : null}
      {modal === "memoDelete" ? <AdminModal title="메모 삭제" description="등록된 메모를 삭제하시겠습니까?" confirmLabel="삭제" cancelLabel="취소" onCancel={() => setModal(null)} onConfirm={async () => {
        if (memoDeleteId === null || !detail) return;
        try {
          await deleteMemberMemo(credentials, detail.memberCode, memoDeleteId);
          setDetail((value) => value ? { ...value, memos: value.memos.filter((memo) => memo.id !== memoDeleteId) } : value);
          setMemoDeleteId(null);
          setModal("memoDeleted");
        } catch (reason) { setError(reason instanceof Error ? reason.message : "메모를 삭제하지 못했습니다."); setModal(null); }
      }} /> : null}
      {modal === "memoDeleted" ? <AdminModal title="삭제 완료" description="메모가 삭제되었습니다." onConfirm={() => setModal(null)} /> : null}
      {modal === "subscriptionCancel" && detail ? <AdminModal title="구독취소" description="해당 회원의 이용 중인 구독권을 취소하시겠습니까? 취소 즉시 구독 서비스 이용이 종료됩니다." confirmLabel="구독취소" cancelLabel="닫기" onCancel={() => setModal(null)} onConfirm={async () => {
        try {
          await cancelMemberSubscription(credentials, detail.memberCode);
          setDetail(await getMember(credentials, detail.memberCode));
          setModal("subscriptionCancelled");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "구독을 취소하지 못했습니다.");
          setModal(null);
        }
      }} /> : null}
      {modal === "subscriptionCancelled" ? <AdminModal title="구독취소 완료" description="회원의 구독 서비스 이용이 종료되었습니다." onConfirm={() => setModal(null)} /> : null}
    </>
  );
}

function HistoryList({ kind }: { kind: "diet" | "recipe" }) {
  const isDiet = kind === "diet";
  const credentials = readAdminCredentials()!;
  const [dietItems, setDietItems] = useState<RecommendationSummary[]>([]);
  const [recipeItems, setRecipeItems] = useState<SavedRecipeSummary[]>([]);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [codeInput, setCodeInput] = useState("");
  const [memberNameInput, setMemberNameInput] = useState("");
  const [detailInput, setDetailInput] = useState("");
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});
  useEffect(() => {
    const request = isDiet
      ? getRecommendations(credentials, pageQuery(pageIndex, searchFilters))
      : getSavedRecipes(credentials, pageQuery(pageIndex, searchFilters));
    void request.then((page) => {
      if (isDiet) setDietItems(page.items as RecommendationSummary[]);
      else setRecipeItems(page.items as SavedRecipeSummary[]);
      setTotal(page.total);
      setTotalPages(page.totalPages);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "이력을 불러오지 못했습니다."));
  }, [isDiet, pageIndex, searchFilters]);
  const rows = isDiet
    ? dietItems.map((item, index) => [String(total - pageIndex * 10 - index).padStart(2, "0"), item.recommendationCode, formatDate(item.requestedAt), item.memberName, item.ingredients, String(item.recipeCount)])
    : recipeItems.map((item, index) => [String(total - pageIndex * 10 - index).padStart(2, "0"), item.savedRecipeCode, formatDate(item.savedAt), item.memberName, item.recipeTitle]);
  return (
    <>
      <SearchPanel title="이력 검색" onSearch={() => {
        setPageIndex(0);
        setSearchFilters(isDiet
          ? { code: codeInput, memberName: memberNameInput, date: detailInput }
          : { code: codeInput, memberName: memberNameInput, recipeTitle: detailInput });
      }}>
        <div className="bt-bo-fields bt-bo-fields-3">
          <Field label="등록 코드" placeholder="ex. BTYYYYMMDD01" value={codeInput} onChange={setCodeInput} />
          <Field label="회원명" placeholder="ex. 홍길동" value={memberNameInput} onChange={setMemberNameInput} />
          {isDiet
            ? <Field label="진행 일자" type="date" placeholder="YYYY.MM.DD" value={detailInput} onChange={setDetailInput} />
            : <Field label="레시피명" placeholder="ex. 돼지고기 카레" value={detailInput} onChange={setDetailInput} />}
        </div>
      </SearchPanel>
      <section className="bt-bo-list-section">
        <SectionTitle>{isDiet ? "식단 추천 이력 리스트" : "레시피 저장 이력 리스트"}</SectionTitle>
        {error ? <p role="alert">{error}</p> : null}
        <DataTable
          headings={isDiet ? ["No", "등록 코드", "진행 일시", "회원명", "냉장고 속 식재료", "추천 레시피"] : ["No", "등록 코드", "저장 일시", "회원명", "레시피명"]}
          rows={rows}
          onOpen={(index) => navigate(isDiet ? "dietDetail" : "recipeDetail", isDiet ? dietItems[index]?.recommendationCode : recipeItems[index]?.savedRecipeCode)}
          columnWidths={isDiet
            ? ["60px", "160px", "170px", "120px", "auto", "120px"]
            : ["60px", "180px", "180px", "140px", "auto"]}
        />
        <Pagination page={pageIndex} totalPages={totalPages} onChange={setPageIndex} />
      </section>
    </>
  );
}

function JsonBlock({ value }: { value: string | null }) {
  if (!value) return <span>-</span>;
  try {
    const parsed = JSON.parse(value) as unknown;
    return <pre>{JSON.stringify(parsed, null, 2)}</pre>;
  } catch {
    return <span>{value}</span>;
  }
}

function RecipeBody({ recipe }: { recipe: RecommendationDetail["recipes"][number] }) {
  return (
    <div className="bt-bo-recipe-copy">
      <strong>{recipe.title}</strong>
      <p>{recipe.summary ?? "-"}</p>
      <p>조리 시간 : {recipe.cookingMinutes ?? "-"}분, 난이도 : {recipe.difficulty ?? "-"}, 분량 : {recipe.servings ?? "-"}인분</p>
      <p><strong>영양 성분</strong></p><JsonBlock value={recipe.nutritionJson} />
      <p><strong>재료</strong></p><JsonBlock value={recipe.ingredientsJson} />
      <p><strong>조리 방법</strong></p><JsonBlock value={recipe.stepsJson} />
    </div>
  );
}

function DietDetail() {
  const [detail, setDetail] = useState<RecommendationDetail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { const code = detailCode(); if (code) void getRecommendation(readAdminCredentials()!, code).then(setDetail).catch((reason) => setError(reason instanceof Error ? reason.message : "추천 상세를 불러오지 못했습니다.")); }, []);
  if (error) return <p role="alert">{error}</p>;
  if (!detail) return <p>추천 상세를 불러오는 중입니다.</p>;
  return (
    <>
      <section className="bt-bo-detail-stack">
        <SectionTitle>기본 정보</SectionTitle>
        <InfoGrid rows={[[["등록 코드", detail.recommendationCode], ["등록 일시", formatDate(detail.requestedAt)]], [["회원명", detail.memberName], ["연락처", detail.phone ?? "-"]]]} />
        <SectionTitle>상세 정보</SectionTitle>
        <InfoGrid rows={[
          [["냉장고 속 재료", detail.ingredients]],
          [["제외할 재료", detail.excludedIngredients ?? "-"]],
          [["함께 식사하는 인원", diningPartySizeLabel(detail.diningPartySize, null, detail.requestedAt)]],
          [["기타 요청사항", detail.request ?? "-"]],
        ]} />
        <SectionTitle>Ai 추천 정보</SectionTitle>
        <InfoGrid rows={[[["레시피 추천 포인트", detail.recommendationPoint ?? "-"]], ...detail.recipes.map((recipe, index) => [[`추천 레시피 ${index + 1}`, <RecipeBody recipe={recipe} />]] as Array<[string, ReactNode]>)]} />
        <div className="bt-bo-center"><BackButton target="dietHistories" /></div>
      </section>
    </>
  );
}

function RecipeDetail() {
  const [detail, setDetail] = useState<SavedRecipeDetail | null>(null);
  const [error, setError] = useState("");
  const returnMemberCode = new URLSearchParams(window.location.search).get("memberCode");
  useEffect(() => { const code = detailCode(); if (code) void getSavedRecipe(readAdminCredentials()!, code).then(setDetail).catch((reason) => setError(reason instanceof Error ? reason.message : "레시피 상세를 불러오지 못했습니다.")); }, []);
  if (error) return <p role="alert">{error}</p>;
  if (!detail) return <p>레시피 상세를 불러오는 중입니다.</p>;
  return (
    <section className="bt-bo-detail-stack">
      <SectionTitle>기본 정보</SectionTitle>
      <InfoGrid rows={[[["등록 코드", detail.savedRecipeCode], ["저장 일시", formatDate(detail.savedAt)]], [["회원명", detail.memberName], ["연락처", detail.phone ?? "-"]]]} />
      <SectionTitle>상세 정보</SectionTitle>
      <InfoGrid rows={[[["레시피 추천 포인트", detail.recommendationPoint ?? "-"]], [["레시피 상세 정보", <RecipeBody recipe={detail.recipe} />]]]} />
      <div className="bt-bo-center">{returnMemberCode
        ? <button className="bt-bo-back" type="button" onClick={() => navigate("members", returnMemberCode)}>뒤로</button>
        : <BackButton target="recipes" />}</div>
    </section>
  );
}

function SubscriptionsPage() {
  const [items, setItems] = useState<PaymentSummary[]>([]);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [paymentCodeInput, setPaymentCodeInput] = useState("");
  const [memberNameInput, setMemberNameInput] = useState("");
  const [planInput, setPlanInput] = useState("전체");
  const [statusInput, setStatusInput] = useState("전체");
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});
  useEffect(() => {
    void getPayments(readAdminCredentials()!, pageQuery(pageIndex, searchFilters)).then((page) => {
      setItems(page.items);
      setTotal(page.total);
      setTotalPages(page.totalPages);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "결제 정보를 불러오지 못했습니다."));
  }, [pageIndex, searchFilters]);
  const rows = items.map((item, index) => [
    String(total - pageIndex * 10 - index).padStart(2, "0"), item.paymentCode, formatDate(item.paidAt), paymentStatus(item.status),
    item.memberName, item.phone ?? "-", item.planName,
    `${formatDate(item.subscriptionStartedAt, true)} - ${formatDate(item.subscriptionEndedAt, true)}`,
    `${item.amountKrw.toLocaleString("ko-KR")}원`, item.method ?? "-",
  ]);
  return (
    <>
      <SearchPanel title="결제 검색" onSearch={() => {
        const planCodes: Record<string, string> = { "구독 1개월": "PLAN-01M", "구독 3개월": "PLAN-03M", "구독 6개월": "PLAN-06M", "구독 12개월": "PLAN-12M" };
        const statuses: Record<string, string> = { "결제 완료": "DONE", "결제 취소": "CANCELLED" };
        setPageIndex(0);
        setSearchFilters({ paymentCode: paymentCodeInput, memberName: memberNameInput, planCode: planCodes[planInput] ?? "", status: statuses[statusInput] ?? "" });
      }}>
        <div className="bt-bo-fields bt-bo-subscription-fields">
          <Field label="결제 번호" placeholder="ex. BTYYMMDD01" value={paymentCodeInput} onChange={setPaymentCodeInput} />
          <Field label="회원명" placeholder="ex. 홍길동" value={memberNameInput} onChange={setMemberNameInput} />
          <Field label="상품명" selectOptions={["전체", "구독 1개월", "구독 3개월", "구독 6개월", "구독 12개월"]} value={planInput} onChange={setPlanInput} />
          <Field label="결제 상태" selectOptions={["전체", "결제 완료", "결제 취소"]} value={statusInput} onChange={setStatusInput} />
        </div>
      </SearchPanel>
      <section className="bt-bo-list-section">
        <SectionTitle>결제 리스트</SectionTitle>
        {error ? <p role="alert">{error}</p> : null}
        <DataTable
          headings={["No", "결제 번호", "결제 일시", "상태", "회원명", "연락처", "상품명", "구독 기간", "총 결제 금액", "결제 수단"]}
          rows={rows}
          onOpen={(index) => navigate("subscriptionDetail", items[index]?.paymentCode)}
          className="bt-bo-subscription-table"
          columnWidths={["50px", "130px", "145px", "80px", "90px", "120px", "100px", "190px", "110px", "100px"]}
        />
        <Pagination page={pageIndex} totalPages={totalPages} onChange={setPageIndex} />
      </section>
    </>
  );
}

function SubscriptionDetail() {
  const credentials = readAdminCredentials()!;
  const [detail, setDetail] = useState<PaymentDetail | null>(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<"cancel" | "cancelled" | "refund" | "refunded" | null>(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [actionError, setActionError] = useState("");
  useEffect(() => { const code = detailCode(); if (code) void getPayment(credentials, code).then(setDetail).catch((reason) => setError(reason instanceof Error ? reason.message : "결제 상세를 불러오지 못했습니다.")); }, []);
  if (error) return <p role="alert">{error}</p>;
  if (!detail) return <p>결제 상세를 불러오는 중입니다.</p>;
  return (
    <section className="bt-bo-detail-stack">
      <SectionTitle>기본 정보</SectionTitle>
      <InfoGrid rows={[[["결제 번호", detail.paymentCode], ["회원명", detail.memberName]], [["연락처", detail.phone ?? "-"], ["회원 상태", memberStatus(detail.memberStatus)]]]} />
      <SectionTitle action={detail.status === "DONE" && !detail.subscriptionCancelledAt && detail.subscriptionEndedAt && new Date(detail.subscriptionEndedAt) > new Date() ? <button className="bt-bo-secondary bt-bo-compact" type="button" onClick={() => setModal("cancel")}>구독취소</button> : detail.subscriptionCancelledAt ? <span className="bt-bo-cancelled-label">구독취소</span> : null}>구독 상품 정보</SectionTitle>
      <InfoGrid rows={[[["상품명", detail.planName], ["구독 기간", `${formatDate(detail.subscriptionStartedAt, true)} - ${formatDate(detail.subscriptionEndedAt, true)}`], ["상품금액 (원)", detail.planPriceKrw.toLocaleString("ko-KR")]]]} />
      <SectionTitle>결제 정보</SectionTitle>
      <InfoGrid rows={[[["주문 번호", detail.orderId], ["결제 키", detail.paymentKey ?? "-"], ["결제 상태", paymentStatus(detail.status)]], [["결제 일시", formatDate(detail.approvedAt)], ["결제 수단", detail.method ?? "-"], ["결제 금액 (원)", detail.amountKrw.toLocaleString("ko-KR")]]]} />
      {detail.subscriptionCancelledAt || detail.status === "CANCELLED" || detail.status === "REFUNDED" ? <>
        <SectionTitle action={detail.refundEligible && detail.refundStatus !== "REFUNDED" && detail.paymentKey
          ? <button className="bt-bo-primary bt-bo-compact" type="button" onClick={() => { setActionError(""); setModal("refund"); }}>환불처리</button>
          : detail.refundStatus === "REFUNDED" ? <span className="bt-bo-cancelled-label">환불완료</span> : null}>환불 정보</SectionTitle>
        <InfoGrid className="bt-bo-refund-grid" rows={[
          [["구독 취소일", formatDate(detail.subscriptionCancelledAt)], ["환불 상태", detail.refundStatus === "REFUNDED" ? "환불완료" : "미환불"], ["환불 처리일", detail.refundProcessedAt ? formatDate(detail.refundProcessedAt) : "-"]],
          [["환불 가능 여부", detail.refundStatus === "REFUNDED" ? "처리완료" : detail.refundEligible ? "가능" : "제한"], ["AI 레시피 제공", `${detail.aiRecipeProvidedCount.toLocaleString("ko-KR")}건`], ["환불 가능 금액 (원)", detail.refundableAmountKrw.toLocaleString("ko-KR")]],
          [["정책 검토 결과", detail.refundEligibilityReason]],
        ]} />
      </> : null}
      {actionError ? <p className="bt-bo-action-error" role="alert">{actionError}</p> : null}
      <div className="bt-bo-center"><BackButton target="subscriptions" /></div>
      {modal === "cancel" ? <AdminModal title="구독취소" description="이 결제건의 이용 중인 구독권을 취소하시겠습니까? 취소 즉시 구독 서비스 이용이 종료됩니다." confirmLabel="구독취소" cancelLabel="닫기" onCancel={() => setModal(null)} onConfirm={async () => {
        try {
          await cancelPaymentSubscription(credentials, detail.paymentCode);
          setDetail(await getPayment(credentials, detail.paymentCode));
          setModal("cancelled");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "구독을 취소하지 못했습니다.");
          setModal(null);
        }
      }} /> : null}
      {modal === "cancelled" ? <AdminModal title="구독취소 완료" description="해당 구독권의 서비스 이용이 종료되었습니다." onConfirm={() => setModal(null)} /> : null}
      {modal === "refund" ? <AdminModal
        title="환불 처리"
        description={processingRefund ? "토스페이먼츠 환불을 처리하고 있습니다." : `결제금액 ${detail.refundableAmountKrw.toLocaleString("ko-KR")}원을 전액 환불하시겠습니까? 처리 후에는 되돌릴 수 없습니다.`}
        confirmLabel={processingRefund ? "처리 중" : "환불처리"}
        cancelLabel="닫기"
        onCancel={() => { if (!processingRefund) setModal(null); }}
        onConfirm={async () => {
          if (processingRefund) return;
          setProcessingRefund(true);
          setActionError("");
          try {
            await refundPayment(credentials, detail.paymentCode);
            setDetail(await getPayment(credentials, detail.paymentCode));
            setModal("refunded");
          } catch (reason) {
            setActionError(reason instanceof Error ? reason.message : "환불을 처리하지 못했습니다.");
            setModal(null);
          } finally {
            setProcessingRefund(false);
          }
        }}
      /> : null}
      {modal === "refunded" ? <AdminModal title="환불 처리 완료" description="토스 결제가 전액 환불되었습니다." onConfirm={() => setModal(null)} /> : null}
    </section>
  );
}

function InquiriesPage({ detail = false }: { detail?: boolean }) {
  const credentials = readAdminCredentials()!;
  const [items, setItems] = useState<InquirySummary[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [codeInput, setCodeInput] = useState("");
  const [titleInput, setTitleInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("전체");
  const [answeredInput, setAnsweredInput] = useState("전체");
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});
  useEffect(() => {
    void getInquiries(credentials, pageQuery(pageIndex, searchFilters)).then((page) => {
      setItems(page.items);
      setTotal(page.total);
      setTotalPages(page.totalPages);
      if (detail) {
        const code = detailCode();
        const index = page.items.findIndex((item) => item.inquiryCode === code);
        if (index >= 0) setSelected(index);
      }
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "문의 정보를 불러오지 못했습니다."));
  }, [detail, pageIndex, searchFilters]);
  useEffect(() => {
    const code = detail ? detailCode() : selected === null ? "" : items[selected]?.inquiryCode;
    if (!code) { setInquiry(null); return; }
    void getInquiry(credentials, code).then((value) => { setInquiry(value); setAnswer(value.answer ?? ""); }).catch((reason) => setError(reason instanceof Error ? reason.message : "문의 상세를 불러오지 못했습니다."));
  }, [detail, selected, items]);
  const rows = items.map((item, index) => [String(total - pageIndex * 10 - index).padStart(2, "0"), item.inquiryCode, item.title, inquiryCategory(item.category), item.memberName, formatDate(item.createdAt), inquiryStatus(item.status)]);
  return (
    <>
      <SearchPanel title="문의 검색" onSearch={() => {
        const categories: Record<string, string> = { "AI 식단 추천 문의": "AI_RECOMMENDATION", "레시피 문의": "RECIPE", "서비스 이용 문의": "SERVICE", "기타 문의": "OTHER" };
        setSelected(null);
        setPageIndex(0);
        setSearchFilters({ code: codeInput, title: titleInput, category: categories[categoryInput] ?? "", answered: answeredInput === "전체" ? "" : answeredInput });
      }}>
        <div className="bt-bo-fields bt-bo-inquiry-fields">
          <Field label="문의 코드" placeholder="ex. BTREQNNNN" value={codeInput} onChange={setCodeInput} />
          <Field label="제목" placeholder="ex. 문의 제목" value={titleInput} onChange={setTitleInput} />
          <Field label="문의 유형" selectOptions={["전체", "AI 식단 추천 문의", "레시피 문의", "서비스 이용 문의", "기타 문의"]} value={categoryInput} onChange={setCategoryInput} />
          <Field label="답변 여부" selectOptions={["전체", "Y", "N"]} value={answeredInput} onChange={setAnsweredInput} />
        </div>
      </SearchPanel>
      <section className="bt-bo-list-section">
        <SectionTitle>문의 리스트</SectionTitle>
        {error ? <p role="alert">{error}</p> : null}
        <DataTable
          headings={["No", "문의 코드", "제목", "문의 유형", "회원명", "등록 일시", "답변 여부"]}
          rows={rows}
          selectedIndex={selected}
          onSelect={setSelected}
          onOpen={(index) => navigate("inquiryDetail", items[index]?.inquiryCode)}
          columnWidths={["60px", "140px", "auto", "160px", "110px", "170px", "84px"]}
        />
        <Pagination page={pageIndex} totalPages={totalPages} onChange={(page) => { setSelected(null); setPageIndex(page); }} />
      </section>
      {inquiry ? (
        <section className="bt-bo-detail-stack bt-bo-inquiry-detail">
          <SectionTitle>문의 상세정보</SectionTitle>
          <InfoGrid rows={[
            [["문의 코드", inquiry.inquiryCode]], [["문의 유형", inquiryCategory(inquiry.category)]], [["회원명", inquiry.memberName]], [["가입일", formatDate(inquiry.memberJoinedAt, true)]], [["등록일", formatDate(inquiry.createdAt)]],
            [["제목", inquiry.title]], [["내용", <div className="bt-bo-long-copy">{inquiry.content}</div>]],
            [["답변", <input value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="답변을 입력해 주세요." />]],
          ]} />
          <div className="bt-bo-center"><button className="bt-bo-primary" type="button" disabled={!answer.trim()} onClick={async () => {
            try { await answerInquiry(credentials, inquiry.inquiryCode, answer.trim()); setInquiry({ ...inquiry, answer: answer.trim(), status: "ANSWERED" }); setSaved(true); }
            catch (reason) { setError(reason instanceof Error ? reason.message : "답변을 저장하지 못했습니다."); }
          }}>저장</button></div>
        </section>
      ) : null}
      {saved ? <AdminModal title="저장 완료" description="답변이 저장되었습니다." onConfirm={() => setSaved(false)} /> : null}
    </>
  );
}

function App() {
  const [view, setView] = useState<View>(viewFromUrl);

  useEffect(() => {
    const handlePopState = () => setView(viewFromUrl());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const content = useMemo(() => {
    switch (view) {
      case "members": return <MembersPage />;
      case "dietHistories": return <HistoryList kind="diet" />;
      case "dietDetail": return <DietDetail />;
      case "recipes": return <HistoryList kind="recipe" />;
      case "recipeDetail": return <RecipeDetail />;
      case "subscriptions": return <SubscriptionsPage />;
      case "subscriptionDetail": return <SubscriptionDetail />;
      case "inquiries": return <InquiriesPage />;
      case "inquiryDetail": return <InquiriesPage detail />;
      default: return null;
    }
  }, [view]);

  if (view === "login" || !readAdminCredentials()) return <AdminLogin />;
  return <AdminShell view={view}>{content}</AdminShell>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
